import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

/**
 * Pagination component for navigating through paginated data
 *
 * A reusable pagination component with Previous/Next navigation
 * and page information display. Follows DRF pagination response structure.
 * Uses Preline bordered style with brand colors.
 *
 * @param {Object} props
 * @param {number} props.currentPage - Current active page number
 * @param {number} props.totalCount - Total number of items
 * @param {number} [props.pageSize=20] - Items per page
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * <Pagination
 *   currentPage={page}
 *   totalCount={data.count}
 *   pageSize={20}
 *   onPageChange={setPage}
 * />
 */
export function Pagination({
  currentPage,
  totalCount,
  pageSize = 20,
  onPageChange,
  className = '',
}) {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const handlePrevious = () => {
    if (hasPrevious) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onPageChange(currentPage + 1);
    }
  };

  // Don't render if only one page or no items
  if (totalPages <= 1) return null;

  return (
    <div className={`flex items-center justify-between gap-x-4 ${className}`.trim()}>
      {/* Results counter */}
      <div className="text-sm text-muted">
        {t('pagination.showing')} <span className="font-medium text-default">{startItem}</span>{' '}
        {t('pagination.to')} <span className="font-medium text-default">{endItem}</span>{' '}
        {t('pagination.of')} <span className="font-medium text-default">{totalCount}</span>{' '}
        {t('pagination.results')}
      </div>

      {/* Navigation - Stretched style (better for many pages) */}
      <nav className="flex justify-between items-center gap-x-1" aria-label="Pagination">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={!hasPrevious}
          className="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-gray-800 hover:bg-brand/5 hover:text-brand focus:outline-hidden focus:bg-brand/5 disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
          aria-label={t('pagination.previous')}
        >
          <Icon icon="lucide:chevron-left" className="shrink-0 size-3.5" />
          <span aria-hidden="true" className="hidden sm:block">
            {t('pagination.previous')}
          </span>
        </button>

        <div className="flex items-center gap-x-1">
          <span className="min-h-9.5 min-w-9.5 flex justify-center items-center border border-gray-200 text-gray-800 py-2 px-3 text-sm rounded-lg">
            {currentPage}
          </span>
          <span className="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm">
            {t('pagination.ofPages')}
          </span>
          <span className="min-h-9.5 flex justify-center items-center text-gray-500 py-2 px-1.5 text-sm">
            {totalPages}
          </span>
        </div>

        <button
          type="button"
          onClick={handleNext}
          disabled={!hasNext}
          className="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-gray-800 hover:bg-brand/5 hover:text-brand focus:outline-hidden focus:bg-brand/5 disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
          aria-label={t('pagination.next')}
        >
          <span aria-hidden="true" className="hidden sm:block">
            {t('pagination.next')}
          </span>
          <Icon icon="lucide:chevron-right" className="shrink-0 size-3.5" />
        </button>
      </nav>
    </div>
  );
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};
